/* import __COLOCATED_TEMPLATE__ from './account-fields.hbs'; */
/* RESPONSIBLE TEAM: team-standalone */

import Component from '@glimmer/component';
import { action } from '@ember/object';
import { inject as service } from '@ember/service';
import type Model from '@ember-data/model';
import { type SyncHasMany } from '@ember-data/model';
import type Store from '@ember-data/store';
import type IntlService from 'ember-intl/services/intl';
import type ExternalFieldIdentifier from 'embercom/models/standalone/external-field-identifier';
import { RESPONSE_PROVIDED_ERRORS } from 'embercom/objects/standalone/constants';
import { type TicketField } from 'embercom/objects/standalone/data-fields/common';
import type FinStandaloneService from 'embercom/services/fin-standalone-service';

export interface Args {
  selectedField?: ExternalFieldIdentifier | TicketField;
  selectField: (field: ExternalFieldIdentifier) => void;
}

type App = { attributes: SyncHasMany<Model>; reload: () => void };

export default class AccountFields extends Component<Args> {
  @service declare appService: { app: App };
  @service declare finStandaloneService: FinStandaloneService;
  @service declare intl: IntlService;
  @service declare notificationsService: {
    notifyResponseError: (
      e: Error,
      messages: { default: string },
      options: { responseProvidedErrors: number[] },
    ) => void;
  };
  @service declare store: Store;

  get app() {
    return this.appService.app;
  }

  get externalFieldIdentifiers(): ExternalFieldIdentifier[] {
    return this.finStandaloneService.salesforcePlatform.companyFields;
  }

  get isLoading() {
    return this.finStandaloneService.salesforcePlatform.isLoadingDataFields;
  }

  get synchronizedFields() {
    // We want to show the built-in fields first in the list.
    return this.externalFieldIdentifiers
      .filter((field) => field.builtIn)
      .concat(
        this.externalFieldIdentifiers.filter((field) => field.synchronized && !field.builtIn),
      );
  }

  get unsynchronizedFields() {
    return this.externalFieldIdentifiers.filter((field) => !field.synchronized);
  }

  @action
  async addField(field: ExternalFieldIdentifier) {
    field.isSynchronizing = true;
    try {
      await field.synchronize();
      this.finStandaloneService.salesforcePlatform.loadDataFields();
      this.app.reload();
    } catch (e) {
      field.rollbackAttributes();
      this.notificationsService.notifyResponseError(
        e,
        {
          default: this.intl.t(
            'standalone.salesforce.setup.pull-data-from-salesforce.error-notification',
          ),
        },
        {
          responseProvidedErrors: RESPONSE_PROVIDED_ERRORS,
        },
      );
      console.error(e.message || e.jqXHR);
      throw e;
    } finally {
      field.isSynchronizing = false;
    }
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'Standalone::Salesforce::DataFields::AccountFields': typeof AccountFields;
    'standalone/salesforce/data-fields/account-fields': typeof AccountFields;
  }
}
